import useSWR, { useSWRConfig } from 'swr'
import useSWRMutation from 'swr/mutation'
import { auditsApi, type AuditsApiError } from '../api/index.ts'
import type { BrainStatus, ChatMessage } from '../types.ts'
import { auditKeys } from './audits.keys.ts'

export function hasUnanswered(messages: ChatMessage[] | undefined): boolean {
  return (messages ?? []).some((m) => m.role === 'user' && m.answeredAt === null)
}

/** The thread, polled every 3 s **only** while a question has no answer (§9.6, D-24). */
export function useChat(auditId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR<ChatMessage[], AuditsApiError>(
    auditId ? auditKeys.chat(auditId) : null,
    async () => {
      const result = await auditsApi.chat(auditId!)
      if (!result.success) throw result.error
      return result.data
    },
    { refreshInterval: (latest) => (hasUnanswered(latest) ? 3000 : 0) },
  )
  return {
    messages: data ?? [],
    waiting: hasUnanswered(data),
    error: error ?? null,
    isLoading,
    refresh: mutate,
  }
}

/** «Brain online» from the heartbeat route; asked with the thread's cadence, and once on arrival. */
export function useBrain(auditId: string | undefined, waiting: boolean) {
  const { data, mutate } = useSWR<BrainStatus, AuditsApiError>(
    auditId ? auditKeys.brain(auditId) : null,
    async () => {
      const result = await auditsApi.brain(auditId!)
      if (!result.success) throw result.error
      return result.data
    },
    { refreshInterval: waiting ? 3000 : 0 },
  )
  return { brain: data ?? null, refresh: mutate }
}

export function useAsk(auditId: string) {
  const { mutate } = useSWRConfig()
  return useSWRMutation(
    auditKeys.chat(auditId),
    async (_key, { arg }: { arg: { text: string } }) => {
      const result = await auditsApi.ask(auditId, arg.text)
      if (!result.success) throw result.error
      await mutate(auditKeys.chat(auditId))
      return result.data
    },
  )
}
